import axios from 'axios'
import { setItemSafely } from 'common_packages/assets/js/utilities/persistencyStorage'
import * as lsKeys from 'common_packages/assets/js/utilities/persistency_storage/localStorageKeys'
import type { TranslationContextProps } from 'client/contexts/TranslationContext'
import translationGroups from 'client/constants/translationGroups'
import { addToast } from 'common_packages/assets/js/actions/uiActions'
import { toastTypes } from 'common_packages/assets/js/types/toastTypes'
import { createStandardPublicClientApplication } from '@azure/msal-browser'
import type { SSOSettings } from 'client/hooks/useConfig'
import { store } from '../store'
import type { ApiResult } from './common'
import { API_URL, klubbleAPI } from './common'
import { type AuthToken } from '../types/AuthToken'
import {
  clearUser,
  setUser,
  setRoles,
  clearRoles,
} from '../actions/userActions'

const CONFIG = {
  headers: {
    'Content-Type': 'application/json',
  },
}

let AUTH_TOKEN: AuthToken = null

export const getCurrentToken = (): AuthToken => {
  if (AUTH_TOKEN) {
    return AUTH_TOKEN
  }

  const authTokenAsString = localStorage.getItem(lsKeys.authToken)
  return authTokenAsString ? JSON.parse(authTokenAsString) : null
}

export const setToken = (token: AuthToken): void => {
  AUTH_TOKEN = token
  setRoles(token?.roles ?? [])(store.dispatch)
  klubbleAPI.defaults.headers.common.Authorization = `Bearer ${token.jwtToken}`
  setItemSafely(lsKeys.authToken, JSON.stringify(token))
}

function clearToken(): void {
  AUTH_TOKEN = null
  clearRoles()(store.dispatch)
  localStorage.removeItem(lsKeys.authToken)
  delete klubbleAPI.defaults.headers.common.Authorization
}

// Extracts JWT token props from API result
function extractToken(data: ApiResult<AuthToken>): AuthToken {
  const {
    jwtToken,
    refreshToken,
    roles,
  } = data.data
  return {
    jwtToken,
    refreshToken,
    roles,
  }
}

export const refreshToken = async (): Promise<AuthToken> => {
  const currentToken = getCurrentToken()
  try {
    const res = await axios.post<ApiResult<AuthToken>>(`${API_URL}Auth/refresh`, currentToken, {
      ...CONFIG,
    })

    if (!res.data?.success) {
      throw new Error('refreshing with refreshToken success = false')
    }

    const newToken = extractToken(res.data)
    setToken(newToken)
    await setUser()(store.dispatch)
    return newToken
  } catch (err) {
    logoutAndLogin()
    throw err
  }
}

export const login = async (translation: TranslationContextProps, email?: string, password?: string): Promise<ApiResult<AuthToken>> => {
  try {
    let res
    if (email && password) {
      const payload = JSON.stringify({ email, password })
      res = await klubbleAPI.post<ApiResult<AuthToken>>('Auth/authenticate', payload, CONFIG)
    } else {
      res = await klubbleAPI.get<ApiResult<AuthToken>>('Auth/loginguest')
    }

    if (res.data.success) {
      const authToken = extractToken(res.data)
      setToken(authToken)
      await setUser()(store.dispatch)
    } else {
      addToast({
        message: translation.getTranslation(translationGroups.form, res.data.message),
        type: toastTypes.ERROR,
      })(store.dispatch)
    }
    return res.data
  } catch {
    addToast({
      message: translation.getTranslation(translationGroups.api, 'something-went-wrong'),
      type: toastTypes.ERROR,
    })(store.dispatch)
    return null
  }
}

export const loginQRHost = async (guid: string): Promise<void> => {
  const res = await klubbleAPI.get(`Auth/loginqrhost?guid=${guid}`)

  if (!res.data.success) {
    throw new Error(`${res.config.url} did not return succesfully`)
  }
  const authToken = res.data.data
  setToken(authToken)

  await setUser()(store.dispatch)
}
export const logoutAndLogin = (): void => {
  // used when the local session is deemed invalid for an unknown reason. throws away the local session and redirect to the login page
  // in case of SSO, if the SSO session is still valid, the session will be refreshed instantly
  localLogout()
  const redirectURI = new URL(`${getRedirectBaseUrl()}/login`)
  redirectURI.searchParams.append('redirect', encodeURIComponent(`${window.location.pathname}${window.location.search}`))
  window.location.href = redirectURI.toString()
}

export const getRedirectBaseUrl = (): string => {
  return process.env.KLUBBLE_API_BASE_PATH.length > 0 ? process.env.KLUBBLE_API_BASE_PATH : window.location.origin
}

export const logout = (errorMessage?: string, customRedirect?:string): void => {
  if (process.env.KLUBBLE_LOGIN_SSO_ENABLED !== 'true') {
    localLogout()
  } else {
    ssoLogout(errorMessage, customRedirect)
  }
}

export const ssoLogout = (errorMessage?: string, customRedirect?: string): void => {
  const redirectUrl = new URL(`${window.location.origin}/logoutSSO`)
  if (errorMessage) {
    redirectUrl.searchParams.append('error', errorMessage)
  }
  const signoutUrl = new URL(`${getRedirectBaseUrl()}/account/signout`)
  signoutUrl.searchParams.append('redirect_after_logout', customRedirect || redirectUrl.toString())
  window.location.href = signoutUrl.toString()
}

export const localLogout = (): void => {
  clearToken()
  clearUser()(store.dispatch)
  // remove possibly existing ls keys related to the payment
  localStorage.removeItem(lsKeys.paymentPeriodId)
  localStorage.removeItem(lsKeys.puzzlePackId)
  localStorage.removeItem(lsKeys.adyenCheckoutSession)
}

export const checkHasActiveSSOSession = async (settings: SSOSettings, valueWhenUndetermined?: boolean): Promise<boolean> => {
  const instanceName = settings.instance.replace('https://', '')
  const msal = await createStandardPublicClientApplication({
    auth: {
      clientId: settings.clientId,
      authority: `${settings.instance}/${settings.domain}/${settings.signUpSignInPolicy}`,
      knownAuthorities: [instanceName],
      redirectUri: `${window.location.origin}/blank.html`,
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
  })

  try {
    await msal.ssoSilent({})
    const account = msal.getAccount({ environment: instanceName })
    const isValidAccount = account?.idTokenClaims?.exp > (Date.now() / 1000)
    return isValidAccount
  } catch (err) {
    if (instanceName.includes('test') || instanceName.includes('feature')) {
      // eslint-disable-next-line no-console
      console.error('error while validating SSO session', err)
    }
    return valueWhenUndetermined === undefined ? true : valueWhenUndetermined
  }
}
